import { useNavigate } from 'react-router-dom';
import { useLinkedCustomer } from '@/hooks/useCustomers';
import { useVisibility } from '@/hooks/useVisibility';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ArrowLeft, Briefcase, User, MapPin, DollarSign, FileText, Trash2, XCircle, Calendar, Clock, MoreHorizontal, Phone, Mail, Users, Building2, CalendarCheck, X, FileCheck, Lock, AlertTriangle, UserCheck } from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { format, addDays } from 'date-fns';
import { formatDocDate } from '@/utils/locale';
import { parseDateInTz } from '@/utils/timezone';
import { LeadActivityTimeline } from '@/components/leads/LeadActivityTimeline';
import { Copy } from 'lucide-react';
import { ConvertToJobWizard } from '@/components/leads/ConvertToJobWizard';
import { ConversionReadinessCard } from '@/components/leads/ConversionReadinessCard';
import { CancelLeadDialog } from '@/components/leads/CancelLeadDialog';
import { FollowUpDialog } from '@/components/leads/FollowUpDialog';
import { EstimateFromLeadDialog } from '@/components/estimates/EstimateFromLeadDialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createLeadFormBaseSchema, type LeadFormData } from '@/schemas/lead.schema';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useUpdateLeadDetails, useCancelLead } from '@/hooks/useLeadActions';
import { useDeleteLead, useClaimLead } from '@/hooks/useLeads';
import { useAuth } from '@/hooks/useAuth';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { AddressVerificationBadge } from '@/components/ui/AddressVerificationBadge';
import type { NormalizedAddress } from '@/utils/parseGoogleAddress';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { useServiceTypes, useTechsByMetro, useCampaigns, useCSRs } from '@/hooks/useReferenceData';
import { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { LeadWithRelations } from '@/types';
import { CampaignAssignment } from '@/components/leads/CampaignAssignment';
import { useCallLogs } from '@/hooks/useCallTracking';
import { getCtmTrackingLabel } from '@/utils/ctmMetadata';


interface LeadDetailContentProps {
  lead: LeadWithRelations;
  /** Whether this is rendered inside a sheet (compact mode) or as full page */
  isSheet?: boolean;
  /** Callback when lead is deleted (for navigation in page context) */
  onDeleted?: () => void;
  /** Callback when the sheet/page should close */
  onClose?: () => void;
}

const statusColors: Record<string, string> = {
  new: 'bg-info',
  processing: 'bg-warning',
  needs_follow_up: 'bg-warning',
  estimate_sent: 'bg-primary',
  estimate_accepted: 'bg-success',
  converted: 'bg-success',
  lost: 'bg-destructive',
};

export function LeadDetailContent({ lead, isSheet = false, onDeleted, onClose }: LeadDetailContentProps) {
  const navigate = useNavigate();
  const { data: customer } = useLinkedCustomer(lead);
  const { canSeeCompanyFinancials } = useVisibility();
  const updateLead = useUpdateLeadDetails();
  const cancelLead = useCancelLead();
  const deleteLead = useDeleteLead();
  const { isAdmin } = useUserRole();
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const [nextFollowUp, setNextFollowUp] = useState<Date | undefined>();

  const { data: serviceTypes = [] } = useServiceTypes();
  const { data: techs = [] } = useTechsByMetro(lead.metro_id);
  const { data: campaigns = [] } = useCampaigns();
  const { data: csrs = [] } = useCSRs();

  const { locale, timezone } = useCompanyLocale();
  const leadSchema = useMemo(() => createLeadFormBaseSchema(locale), [locale]);

  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      customer_type: 'individual',
      customer_name: '',
      company_name: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      customer_suburb: '',
      customer_state: '',
      customer_postcode: '',
      issue_description: '',
      service_type_id: '',
      lead_source_id: '',
      campaign_id: '',
      quoted_price: '',
      scheduled_date: '',
      scheduled_time: '',
      notes: '',
      assigned_tech_id: '',
      assigned_csr_id: '',
      lead_owner_id: '',
      call_classification: 'service_lead',
    },
  });

  useEffect(() => {
    if (lead) {
      form.reset({
        customer_type: (lead.customer_type || 'individual') as 'individual' | 'company',
        customer_name: lead.customer_name,
        company_name: lead.company_name || '',
        customer_phone: lead.customer_phone,
        customer_email: lead.customer_email || '',
        customer_address: lead.customer_address,
        customer_suburb: lead.customer_suburb || '',
        customer_state: lead.customer_state || '',
        customer_postcode: lead.customer_postcode || '',
        issue_description: lead.issue_description,
        service_type_id: lead.service_type_id || '',
        lead_source_id: lead.lead_source_id || '',
        campaign_id: lead.campaign_id || '',
        quoted_price: lead.quoted_price?.toString() || '',
        scheduled_date: lead.scheduled_date ? formatDocDate(lead.scheduled_date, 'yyyy-MM-dd', timezone) : '',
        scheduled_time: lead.scheduled_time || '',
        notes: lead.notes || '',
        assigned_tech_id: lead.assigned_tech_id || '',
        assigned_csr_id: lead.assigned_csr_id || '',
        lead_owner_id: lead.lead_owner_id || '',
        call_classification: (lead as any).call_classification || 'service_lead',
      });
      setNextFollowUp(lead.next_follow_up_date ? new Date(lead.next_follow_up_date) : undefined);
    }
  }, [lead?.id]);

  const isDirty = form.formState.isDirty;

  const handleSave = (data: LeadFormData) => {
    updateLead.mutate({
      leadId: lead.id,
      updates: {
        customer_type: data.customer_type,
        customer_name: data.customer_name,
        company_name: data.company_name || null,
        customer_phone: data.customer_phone,
        customer_email: data.customer_email || null,
        customer_address: data.customer_address,
        customer_suburb: data.customer_suburb || null,
        customer_state: data.customer_state || null,
        customer_postcode: data.customer_postcode || null,
        issue_description: data.issue_description,
        service_type_id: data.service_type_id || null,
        lead_source_id: data.lead_source_id || null,
        campaign_id: data.campaign_id || null,
        quoted_price: data.quoted_price ? parseFloat(data.quoted_price) : null,
        // Midnight in the company timezone; a bare date would be stored as midnight UTC.
        scheduled_date: data.scheduled_date ? parseDateInTz(data.scheduled_date, timezone).toISOString() : null,
        scheduled_time: data.scheduled_time || null,
        notes: data.notes || null,
        assigned_tech_id: data.assigned_tech_id || null,
        assigned_csr_id: data.assigned_csr_id || null,
        lead_owner_id: data.lead_owner_id || null,
        call_classification: data.call_classification || 'service_lead',
        lat: data.lat ?? null,
        lng: data.lng ?? null,
      },
    }, {
      onSuccess: () => {
        form.reset(data);
      },
    });
  };

  const customerType = form.watch('customer_type');

  const handleDeleteLead = () => {
    deleteLead.mutate(lead.id, {
      onSuccess: () => {
        onDeleted?.();
      },
    });
  };

  const handleCancelLead = (reason: string) => {
    cancelLead.mutate({
      leadId: lead.id,
      reason,
    }, {
      onSuccess: () => {
        setShowCancelDialog(false);
      },
    });
  };

  const handleCallStatusChange = (value: string) => {
    updateLead.mutate({
      leadId: lead.id,
      updates: { call_status: value },
    });
  };

  const handleFollowUpDateChange = (date: Date | undefined) => {
    setNextFollowUp(date);
    updateLead.mutate({
      leadId: lead.id,
      updates: { next_follow_up_date: date?.toISOString() || null },
    });
  };

  const handleQuickFollowUp = (days: number) => {
    const date = addDays(new Date(), days);
    setNextFollowUp(date);
    updateLead.mutate({
      leadId: lead.id,
      updates: {
        next_follow_up_date: date.toISOString(),
        call_status: 'callback_requested',
        status: 'needs_follow_up',
      },
    });
  };

  const handleClearFollowUp = () => {
    setNextFollowUp(undefined);
    updateLead.mutate({
      leadId: lead.id,
      updates: { next_follow_up_date: null },
    });
  };


  const { user } = useAuth();
  const { mutate: claimLead, isPending: isClaiming } = useClaimLead();

  const isWebhookLead = lead?.lead_entry_type === 'webhook';
  const assignedCSRId = form.watch('assigned_csr_id');
  const isCSRLocked = isWebhookLead && !!assignedCSRId;
  const isUnassigned = lead?.assigned_csr_id == null;
  const csrName = lead?.csr_profile?.full_name;

  const { data: callLogsForLabel } = useCallLogs({
    leadId: lead.id,
    limit: 20,
    enabled: isWebhookLead,
  });

  const ctmTrackingLabel = useMemo(() => {
    if (!callLogsForLabel?.length) return null;
    for (const c of callLogsForLabel) {
      const label = getCtmTrackingLabel(c.metadata);
      if (label) return label;
    }
    return null;
  }, [callLogsForLabel]);

  const handleClaimLead = () => {
    if (!user?.id) return;
    claimLead(
      { leadId: lead.id, userId: user.id },
      {
        onSuccess: () => {
          form.setValue('assigned_csr_id', user.id);
        },
      }
    );
  };

  // Determine which single banner to show (priority order)
  const getBannerContent = () => {
    // Unassigned Pool banner has highest priority (before converted/lost banners)
    if (isUnassigned && lead.status !== 'converted' && lead.status !== 'lost') {
      return (
        <Card className="border-warning/20 bg-warning/5">
          <CardContent className="py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-warning/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-warning">
                    Unassigned — In the Pool
                  </h3>
                  <p className="text-xs text-warning">
                    No CSR has claimed this lead yet. Claim it or take any action (call, note, SMS) to auto-assign it to yourself.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleClaimLead}
                disabled={isClaiming}
                className="shrink-0 bg-warning hover:bg-warning/90 text-primary-foreground"
              >
                <UserCheck className="h-3.5 w-3.5 mr-1.5" />
                {isClaiming ? 'Claiming...' : 'Claim Lead'}
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (lead.status === 'converted') {
      return (
        <Card className="border-success/30 bg-success/5">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center">
                  <Briefcase className="h-4 w-4 text-success" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-success">
                    Converted to Job
                  </h3>
                  <p className="text-xs text-success">
                    {lead.converted_at ? format(new Date(lead.converted_at), 'PPP') : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-success">Change Status To:</span>
                  <Select
                    onValueChange={(value) => {
                      if (confirm(`Change status to ${value}?`)) {
                        updateLead.mutate({
                          leadId: lead.id,
                          updates: {
                            status: value,
                            job_id: null,
                            converted_at: null,
                            lost_reason: null,
                            call_status: 'callback_requested'
                          },
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 w-[130px] bg-background/50 border-success/20">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {lead.job_id && (
                  <Button onClick={() => navigate(`/jobs/${lead.job_id}`)} size="sm" className="gap-2 h-8">
                    View Job
                    <ArrowLeft className="h-3 w-3 rotate-180" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (lead.status === 'lost') {
      return (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-destructive">Lead Lost</h3>
                  {lead.lost_reason && (
                    <p className="text-xs text-muted-foreground">Reason: {lead.lost_reason}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Change Status To:</span>
                <Select
                  onValueChange={(value) => {
                    if (confirm(`Change status to ${value}?`)) {
                      updateLead.mutate({
                        leadId: lead.id,
                        updates: {
                          status: value,
                          lost_reason: null,
                          job_id: null,
                          converted_at: null,
                          call_status: 'callback_requested'
                        },
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-8 w-[130px] bg-background">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Show conversion readiness only if not converted/lost
    return <ConversionReadinessCard lead={lead} />;
  };

  return (
    <Form {...form}>
      <div className="space-y-4">
        {/* Header with actions */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className={cn("font-bold", isSheet ? "text-2xl" : "text-2xl")}>{lead.lead_number}</h1>
            <Badge className={statusColors[lead.status]} variant="default">
              {lead.status.replace('_', ' ').toUpperCase()}
            </Badge>
            {isDirty && (
              <Badge variant="secondary" className="gap-1">
                <FileText className="h-3 w-3" />
                Unsaved
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Primary Actions */}
            <Button
              onClick={form.handleSubmit(handleSave)}
              disabled={!isDirty || updateLead.isPending}
              size="sm"
              variant={isDirty ? "default" : "outline"}
              className="gap-2"
            >
              {updateLead.isPending ? 'Saving...' : 'Save'}
            </Button>

            {lead.status !== 'converted' && lead.status !== 'lost' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFollowUpDialog(true)}
                  className="gap-2"
                >
                  <Clock className="h-4 w-4" />
                  Follow-up
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEstimateDialog(true)}
                  className="gap-2"
                >
                  <FileCheck className="h-4 w-4" />
                  Create Estimate
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowConvertDialog(true)}
                  className="gap-2 bg-success hover:bg-success/90"
                >
                  <Briefcase className="h-4 w-4" />
                  Book Job
                </Button>
              </>
            )}

            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={async () => {
                  const formatLeadForCopy = () => {
                    const scheduledDate = lead.scheduled_date
                      ? formatDocDate(lead.scheduled_date, 'PPP', timezone)
                      : 'TBD';
                    const scheduledTime = lead.scheduled_time || 'TBD';
                    const quote = lead.quoted_price ? `$${lead.quoted_price.toFixed(2)}` : 'TBD';
                    const serviceType = lead.service_types?.name || 'N/A';
                    const leadSource = lead.lead_sources?.name || 'N/A';
                    return `📋 Lead: ${lead.lead_number}
Status: ${lead.status.toUpperCase()}

👤 Customer: ${lead.customer_name}
📞 Phone: ${lead.customer_phone}
${lead.customer_email ? '📧 Email: ' + lead.customer_email + '\n' : ''}📍 Address: ${lead.customer_address}

🔧 Service Type: ${serviceType}
📍 Lead Source: ${leadSource}
🕐 Scheduled: ${scheduledDate} at ${scheduledTime}
💰 Quote: ${quote}

Issue Description:
${lead.issue_description}${lead.notes ? '\n\nNotes:\n' + lead.notes : ''}`;
                  };
                  await navigator.clipboard.writeText(formatLeadForCopy());
                }} className="cursor-pointer">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Details
                </DropdownMenuItem>

                {lead.status !== 'converted' && lead.status !== 'lost' && (
                  <DropdownMenuItem
                    onClick={() => setShowCancelDialog(true)}
                    className="text-destructive focus:text-destructive cursor-pointer"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Mark as Lost
                  </DropdownMenuItem>
                )}

                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive focus:text-destructive cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Lead
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Single Smart Banner */}
        <div>{getBannerContent()}</div>

        {/* Campaign Assignment (only if no campaign) */}
        <CampaignAssignment
          leadId={lead.id}
          currentCampaignId={lead.campaign_id}
        />

        {/* Customer card link — shown for every status (booked/lost leads are the ones most likely to have a customer) */}
        {customer && (
          <div className="flex flex-wrap items-center gap-2 text-sm px-1">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Customer:</span>
            <Button
              variant="link"
              onClick={() => { onClose?.(); navigate(`/customers/${customer.id}`); }}
              className="p-0 h-auto text-sm font-medium hover:underline"
            >
              {customer.customer_name}
            </Button>
            <span className="text-muted-foreground">
              ({customer.total_jobs_count} jobs{canSeeCompanyFinancials ? `, ${customer.total_lifetime_value?.toFixed(0) || 0} LTV` : ''})
            </span>
            {!lead.customer_id && (
              <span className="text-xs text-muted-foreground">· matched by phone</span>
            )}
          </div>
        )}

        {/* Tabbed Layout */}
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="details" className="gap-2">
              <FileText className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="scheduling" className="gap-2">
              <Calendar className="h-4 w-4" />
              Scheduling
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-0">
            <Card>
              <CardContent className="pt-4 space-y-4">
                {/* Customer Section - Combined Contact & Address */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <User className="h-3.5 w-3.5" />
                    Customer
                  </h3>
                  {/* Row 1: Name, Phone, Email, Type */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <FormField
                      control={form.control}
                      name="customer_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Name *</FormLabel>
                          <FormControl>
                            <Input {...field} className="h-8 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Phone *</FormLabel>
                          <FormControl>
                            <Input {...field} className="h-8 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Email</FormLabel>
                          <FormControl>
                            <Input type="email" {...field} className="h-8 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="individual">Individual</SelectItem>
                              <SelectItem value="company">Company</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  {customerType === 'company' && (
                    <FormField
                      control={form.control}
                      name="company_name"
                      render={({ field }) => (
                        <FormItem className="max-w-xs">
                          <FormLabel className="text-xs">Company Name</FormLabel>
                          <FormControl>
                            <Input {...field} className="h-8 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  {/* Row 2: Street Address, Suburb, State, Postcode */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <FormField
                      control={form.control}
                      name="customer_address"
                      render={({ field }) => (
                        <FormItem className="col-span-2 md:col-span-3">
                          <FormLabel className="text-xs">Street Address</FormLabel>
                          <FormControl>
                            <AddressAutocomplete
                              id="lead-detail-customer-address"
                              value={field.value ?? ''}
                              onChange={field.onChange}
                              onAddressSelected={(normalized: NormalizedAddress) => {
                                const setIfChanged = (name: keyof LeadFormData, val: string) => {
                                  if (form.getValues(name) !== val) {
                                    form.setValue(name, val as never, { shouldDirty: true });
                                  }
                                };
                                setIfChanged('customer_address', normalized.street);
                                if (normalized.suburb)   setIfChanged('customer_suburb', normalized.suburb);
                                if (normalized.state)    setIfChanged('customer_state', normalized.state);
                                if (normalized.postcode) setIfChanged('customer_postcode', normalized.postcode);
                                // Coordinates: silent, no dirty flag
                                if (normalized.lat != null) form.setValue('lat', normalized.lat, { shouldDirty: false });
                                if (normalized.lng != null) form.setValue('lng', normalized.lng, { shouldDirty: false });
                              }}
                              disabled={field.disabled}
                              className="h-8 text-sm"
                              placeholder="Start typing a street address…"
                            />
                          </FormControl>
                          <AddressVerificationBadge
                            address={field.value}
                            lat={form.watch('lat')}
                            lng={form.watch('lng')}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_suburb"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Suburb</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Sydney" className="h-8 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">State</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="e.g., VIC" className="h-8 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="customer_postcode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Postcode</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="2000" maxLength={4} className="h-8 text-sm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t" />

                {/* Service Section */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Briefcase className="h-3.5 w-3.5" />
                    Service
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="service_type_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Service Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {serviceTypes.map((type) => (
                                <SelectItem key={type.id} value={type.id}>
                                  {type.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="quoted_price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Quoted Price</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                              <Input type="number" step="0.01" {...field} className="h-8 text-sm pl-8" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t" />

                {/* Issue & Notes Section */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5" />
                    Issue & Notes
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="issue_description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Issue Description *</FormLabel>
                          <FormControl>
                            <Textarea {...field} rows={3} className="text-sm resize-none" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Internal Notes</FormLabel>
                          <FormControl>
                            <Textarea {...field} rows={3} className="text-sm resize-none" placeholder="Add any internal notes here..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Meta info */}
                <div className="border-t pt-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                    <span>Created: {format(new Date(lead.created_at), 'MMM d, yyyy')}</span>
                    <span>Updated: {format(new Date(lead.updated_at), 'MMM d, yyyy')}</span>
                    {lead.campaigns?.name && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-warning/15 text-warning font-medium border border-warning/20">
                        <Lock className="h-3 w-3" />
                        <span className="text-warning/70 font-normal">Campaign:</span> {lead.campaigns.name}
                      </span>
                    )}
                    {lead.form_name && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted text-muted-foreground font-medium border">
                        <span className="font-normal">Web form:</span> {lead.form_name}
                      </span>
                    )}
                    {ctmTrackingLabel && (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-info/10 text-info font-medium border border-info/20 max-w-full min-w-0"
                        title={ctmTrackingLabel}
                      >
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="text-info/80 font-normal shrink-0">Tracking label:</span>
                        <span className="truncate">{ctmTrackingLabel}</span>
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activity Timeline */}
            <LeadActivityTimeline leadId={lead.id} />
          </TabsContent>

          {/* Scheduling & Assignment Tab */}
          <TabsContent value="scheduling" className="space-y-0">
            <Card>
              <CardContent className="pt-6 space-y-6">
                {/* Assignment Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Assignment
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="assigned_csr_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Assigned CSR</FormLabel>
                          {isCSRLocked ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 h-9 px-3 rounded-md border bg-muted/50">
                                <span className="font-medium text-sm">{csrName || 'Unknown'}</span>
                                <Badge variant="outline" className="ml-auto text-xs">
                                  <Lock className="h-3 w-3 mr-1" />
                                  Call Handler
                                </Badge>
                              </div>
                              <FormDescription className="text-xs">
                                Auto-assigned based on who answered the call
                              </FormDescription>
                            </div>
                          ) : (
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Select CSR (optional)" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {csrs.map((csr) => (
                                  <SelectItem key={csr.id} value={csr.id}>
                                    {csr.full_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="assigned_tech_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Pre-assign Technician</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select technician (optional)" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {techs.map((tech) => (
                                <SelectItem key={tech.id} value={tech.id}>
                                  {tech.name}
                                </SelectItem>
                              ))}
                              {/* Show current tech if filtered out by metro */}
                              {lead.assigned_tech_id && !techs.find(t => t.id === lead.assigned_tech_id) && (
                                <SelectItem value={lead.assigned_tech_id} className="text-warning">
                                  {lead.tech_profile?.name ?? 'Current tech'} (outside metro)
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t" />

                {/* Scheduling Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Scheduling
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="scheduled_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Scheduled Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="scheduled_time"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Time Window</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="e.g., 10:00 AM - 12:00 PM" className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="border-t" />

                {/* Follow-up Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Follow-up
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Quick Follow-up Buttons */}
                    <div className="space-y-2">
                      <Label className="text-xs">Quick Follow-up</Label>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickFollowUp(1)}
                          className="text-xs"
                          type="button"
                        >
                          <Clock className="h-3 w-3 mr-1" />
                          Tomorrow
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickFollowUp(3)}
                          className="text-xs"
                          type="button"
                        >
                          <CalendarCheck className="h-3 w-3 mr-1" />
                          In 3 Days
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickFollowUp(7)}
                          className="text-xs"
                          type="button"
                        >
                          <CalendarCheck className="h-3 w-3 mr-1" />
                          Next Week
                        </Button>
                        {nextFollowUp && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClearFollowUp}
                            className="text-xs text-muted-foreground"
                            type="button"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Call Status</Label>
                        <Select
                          value={lead.call_status || 'not_called'}
                          onValueChange={handleCallStatusChange}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background z-50">
                            <SelectItem value="not_called">Not Called</SelectItem>
                            <SelectItem value="no_answer">No Answer</SelectItem>
                            <SelectItem value="voicemail">Voicemail Left</SelectItem>
                            <SelectItem value="spoke_to_customer">Spoke to Customer</SelectItem>
                            <SelectItem value="callback_requested">Callback Requested</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs">Next Follow-up</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              type="button"
                              className={cn(
                                'w-full justify-start text-left font-normal h-9',
                                !nextFollowUp && 'text-muted-foreground'
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {nextFollowUp ? format(nextFollowUp, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-background z-50" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={nextFollowUp}
                              onSelect={handleFollowUpDateChange}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {lead.last_contact_date && (
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Last Contact</Label>
                          <p className="text-sm h-9 flex items-center">
                            {format(new Date(lead.last_contact_date), 'PPp')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      {/* Dialogs */}
      <ConvertToJobWizard
        open={showConvertDialog}
        onOpenChange={setShowConvertDialog}
        lead={lead}
      />

      <CancelLeadDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={handleCancelLead}
      />

      <FollowUpDialog
        leadId={lead.id}
        firstFollowUpAt={lead.first_follow_up_at}
        open={showFollowUpDialog}
        onOpenChange={setShowFollowUpDialog}
      />

      <EstimateFromLeadDialog
        open={showEstimateDialog}
        onOpenChange={setShowEstimateDialog}
        lead={{
          id: lead.id,
          customer_name: lead.customer_name,
          customer_phone: lead.customer_phone,
          customer_email: lead.customer_email,
          customer_address: lead.customer_address,
          issue_description: lead.issue_description,
          quoted_price: lead.quoted_price,
          customer_id: lead.customer_id,
        }}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLead}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
}
